import { monthCodeToMonthString } from "@package/grid-utils";
import type { Column, GridApi } from "ag-grid-community";
import { useRef } from "react";
import { isNullish } from "remeda";

import { getCurrentCellValueById, getProcessedSelectedCellsByRange, type TRangeCell } from "../../../tableUtils";
import { useGridSettingsIncrementMap, useGridSettingsStatusMap } from "../../../data";
import { type TOptimisticCell, useCalcs } from "../../calcs/react";
import { getManualInstrumentType } from "../statuses/statusLogic";
import { defaultColumnSettings } from "../columns";
import { parseNumber } from "../../numbers";
import { undoRedoAtom } from "../undo-redo";
import { colParams } from "../../utils";
import { store } from "../../modal";

// Time to keep cached increment values before clearing them.
// This allows rapid successive increments while ensuring manual edits take effect.
const CACHE_CLEAR_TIMEOUT_MS = 1000;

export const useUpdateCellByValueAndOperation = () => {
  const incrementMap = useGridSettingsIncrementMap();
  const statusMap = useGridSettingsStatusMap();
  const lastValuesRef = useRef(new Map<string, number>());
  const timeoutRef = useRef(new Map<string, NodeJS.Timeout>());
  const calcs = useCalcs();

  const getCellKey = (cell: TRangeCell) => `${cell.productId}-${cell.rowId}-${cell.columnId}`;

  const getCellMeta = (cell: TRangeCell) => {
    const offset = cell.rowIndex;
    const monthCode = cell.rowId;
    const monthString = monthCodeToMonthString(monthCode);
    return { offset, monthCode, monthString };
  };

  const isOffsetValid = (offset: number) => offset === 0 || !!offset;

  const getStorageType = (productId: string | undefined) => {
    const status = statusMap?.[productId ?? ""];
    return getManualInstrumentType(status);
  };

  const buildEdit = (cell: TRangeCell, value: number | undefined, currentValue: number | undefined, force: boolean) => {
    const { offset, monthCode, monthString } = getCellMeta(cell);
    const storageType = getStorageType(cell.productId);
    if (!isOffsetValid(offset) || !cell.productId || !storageType) return;
    return {
      product: cell.productId,
      offset,
      field: "value",
      result: value ?? null,
      currentValue,
      storageType,
      month: monthString,
      rowId: monthCode,
      force,
      edited_at: Date.now(),
    } satisfies TOptimisticCell & { currentValue: number | undefined };
  };

  const getNewValue = (currentValue: number, increment: number, operation: "+" | "-" | "=") => {
    if (operation === "=") return currentValue;
    return operation === "+" ? currentValue + increment : currentValue - increment;
  };

  return function updateCellByValueAndOperation({
    api,
    operation,
    clearSelection,
    reselect,
  }: {
    api: GridApi | null | undefined;
    operation: "+" | "-" | "=";
    clearSelection?: boolean;
    reselect?: {
      index: number;
      column: Column;
    };
  }) {
    if (!api) return;
    const cells = getProcessedSelectedCellsByRange({ api }).flat();

    if (clearSelection) api.clearRangeSelection();
    if (reselect) {
      api.addCellRange({
        columns: [reselect.column],
        rowStartIndex: reselect.index,
        rowEndIndex: reselect.index,
      });
    }
    if (!cells?.length) return;

    const sharedCells = cells
      .reduce((acc, cell) => {
        const columnParams = colParams(api.getColumn(cell.columnId));
        const shared = columnParams?.sharedCellOffsets?.includes(cell.rowIndex?.toString());
        const value = getCurrentCellValueById(cell.columnId, cell.rowId, api);
        if (
          shared &&
          (!isNullish(value) || operation === "=") &&
          !acc.find((c) => c.rowId === cell.rowId && c.columnId === cell.columnId)
        ) {
          acc.push(cell);
        }
        return acc;
      }, [] as TRangeCell[])
      .sort((a, b) => a.rowIndex - b.rowIndex);

    const edits = sharedCells
      .map((cell) => {
        const columnIncrement = incrementMap?.[cell.columnId] || defaultColumnSettings.increment;
        const cellKey = getCellKey(cell);

        const getGridCellValue = () => {
          const value = getCurrentCellValueById(cell.columnId, cell.rowId, api);
          return parseNumber(value);
        };

        // Use cached value if available (for rapid successive increments), otherwise read from grid
        const currentValue = lastValuesRef.current.has(cellKey)
          ? (lastValuesRef.current.get(cellKey) ?? null)
          : getGridCellValue();

        if (currentValue === null || currentValue === undefined) {
          if (operation !== "=") return;
          return buildEdit(cell, undefined, undefined, true);
        }

        const newValue = getNewValue(currentValue, columnIncrement, operation);
        lastValuesRef.current.set(cellKey, newValue);

        const existingTimeout = timeoutRef.current.get(cellKey);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        const timeout = setTimeout(() => {
          lastValuesRef.current.delete(cellKey);
          timeoutRef.current.delete(cellKey);
        }, CACHE_CLEAR_TIMEOUT_MS);
        timeoutRef.current.set(cellKey, timeout);

        if (!isOffsetValid(cell.rowIndex) || (newValue === currentValue && operation !== "=")) {
          console.warn("Invalid cell edit request", {
            offset: cell.rowIndex,
            oldValue: currentValue,
          });
          return;
        }

        const value = parseNumber(newValue);
        return buildEdit(cell, value ?? undefined, currentValue, operation === "=");
      })
      .filter(Boolean);

    if (operation !== "=") {
      store.set(undoRedoAtom, {
        undo: edits.map((edit) => ({
          ...edit,
          pasted: false,
          result: edit.currentValue ?? null,
        })),
        redo: edits.map((edit) => ({ ...edit, pasted: false })),
        time: Date.now(),
      });
    }

    calcs.cellEdit(...edits);
  };
};
